'use client';

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/services/query/keys';
import { ApiError } from '@/services/http/error-handler';
import { analyticsApi } from './api';
import type { MeetingAnalytics } from './types';

/**
 * Per-meeting analytics (GET via the server proxy). react-query owns the cache,
 * cancels the request on unmount / id change (the AbortSignal is threaded into
 * the fetch), and only auto-retries genuine server faults — a 401/403/404 is
 * surfaced immediately as an inline error rather than hammered.
 */
export function useMeetingAnalytics(meetingId: string) {
  return useQuery<MeetingAnalytics, ApiError>({
    queryKey: queryKeys.analytics.detail(meetingId),
    queryFn: ({ signal }) => analyticsApi.get(meetingId, { signal }),
    enabled: !!meetingId,
    staleTime: 15_000,
    retry: (failureCount, error) => error.status >= 500 && failureCount < 2,
  });
}
